import { Form } from "react-bootstrap";
import { CloseSvg } from "../../CommonComponents/svgs/Svgs";
import "../Catalogs.scss";
import { Fragment, useEffect, useMemo, useState } from "react";
import { containsSpecialChars, getCharacterLimitMessage, getInvalidRangeErrorMessage, getSpecialCharacterMessage, onKeyDown, showToastMessage } from "../../../utils/commonFunctions";
import { GradientBorderSvg } from "../Svgs/CatalogSvgs";
import { CatalogConstants, DEFAULT_INTERFACES, DEFAULT_POWER_SINK_DATA, DEFAULT_POWER_SINK_FIELDS, POWER_SINK_COMPONENTS } from "../utils/constants";
import PayloadForm from "./PayloadForm";
import { checkNumberInputValidation, getLoadingButtonText, getUpdatedFormData, isFormValid, validateForm } from "../utils";
import EdgeForm from "./EdgeForm";
import BusCompForm from "./BusCompForm";
import ConfirmationDialog from "../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import { isEmpty } from "lodash";
import CatalogueService from "../../../service/CatalogueService";

interface CreateCatalogProps {
    formData: any;
    editedCatalogData: { [key: string]: any } | null;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
    additionalConfigurations: { [key: string]: string }[];
    setAdditionalConfigurations: React.Dispatch<React.SetStateAction<{ [key: string]: string }[]>>;
    mode: "Create" | "Update" | "Clone" | null;
    componentType: string;
    setOpenConfigurationModal: React.Dispatch<React.SetStateAction<"Create" | "Update" | "Clone" | null>>;
    setRefreshCatalog: () => void
    modalLoader: boolean
    setModalLoader: React.Dispatch<React.SetStateAction<boolean>>
}

const CreateCatalog = ({ componentType, setOpenConfigurationModal, setRefreshCatalog, mode, formData, setFormData, additionalConfigurations, setAdditionalConfigurations, editedCatalogData, modalLoader, setModalLoader }: CreateCatalogProps) => {
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const has_invalid_specification = useMemo(() => additionalConfigurations.some(object => Object.entries(object).some(([key, value]) => key === '' || value === '')), [additionalConfigurations])
    const catalogue_type = useMemo(() => componentType === CatalogConstants.Payload ? 'Payload' : componentType === CatalogConstants.Edge ? 'Edge' : formData?.catalogueType, [formData, componentType])
    const power_sinks = useMemo(() => formData?.power_sinks || [], [formData?.power_sinks])
    const disabled_add_power_sinks_button = useMemo(() => {
        return power_sinks.some(item =>
            isEmpty(item.name?.trim()) ||
            isEmpty(item.idle_power?.trim()) ||
            isEmpty(item.avg_power?.trim()) ||
            isEmpty(item.peak_power?.trim())
        );
    }, [power_sinks]);

    const Component = useMemo(() => componentType === CatalogConstants.Payload ? PayloadForm : componentType === CatalogConstants.Edge ? EdgeForm : BusCompForm, [componentType])

    useEffect(() => {
        if (POWER_SINK_COMPONENTS.includes(catalogue_type) && catalogue_type !== 'iADCS' && mode === 'Create') {
            setFormData((prev) => ({
                ...prev,
                power_sinks: [DEFAULT_POWER_SINK_DATA]
            }));
        } else if (catalogue_type === 'iADCS') {
            setFormData((prev) => {
                if (prev?.power_sinks) delete prev?.power_sinks
                return prev
            })
        }
    }, [catalogue_type, mode])

    const onChange = (name: string, value: string) => {
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const validateNameField = (value: string) => {
        const max_length = 26;
        if (containsSpecialChars(value)) {
            const message = getSpecialCharacterMessage()
            showToastMessage('info', message)
            return false
        } else if (value.length > max_length) {
            const message = getCharacterLimitMessage(max_length)
            showToastMessage('info', message)
            return false
        } else {
            return true
        }
    }

    const handleNameChange = (name: string, value: string) => {
        if (!validateNameField(value)) return
        onChange(name, value);
    }

    const updateField = (field: string, value: any) => {
        if (!checkNumberInputValidation(value, 5)) return;
        onChange(field, value);
    };

    const onChangeSize = (value: string) => {
        const maxSize = componentType === 'Payload' ? 2000 : 50
        if (Number(value) >= 0 && Number(value) <= maxSize) {
            updateField('size', value);
        } else if (Number(value) > maxSize) {
            showToastMessage('info', getInvalidRangeErrorMessage('Size', 0, maxSize, '(U)'));
        }
    }

    const onChangePowerSink = (index: number, field: string, value: any) => {
        if (field === 'name' && validateNameField(value)) {
            setFormData((prev) => ({
                ...prev,
                power_sinks: [
                    ...prev.power_sinks.slice(0, index),
                    { ...prev.power_sinks[index], [field]: value },
                    ...prev.power_sinks.slice(index + 1)
                ]
            }));
        }
        else if (field !== 'name' && !isNaN(value) && value.length <= 5) {
            setFormData((prev) => ({
                ...prev,
                power_sinks: [
                    ...prev.power_sinks.slice(0, index),
                    { ...prev.power_sinks[index], [field]: value },
                    ...prev.power_sinks.slice(index + 1)
                ]
            }));
        } else if (field !== 'name' && value.length > 5) {
            showToastMessage("info", "Maximum 5 digits allowed");
        }
    }

    const toggleSelection = (interfaceName: string) => {
        if (!formData["interfaces"]) {
            setFormData({
                ...formData,
                interfaces: [interfaceName]
            });
        }
        else if ((formData["interfaces"] || []).includes(interfaceName)) {
            setFormData({
                ...formData,
                interfaces: (formData["interfaces"] as string[]).filter((i) => i !== interfaceName)
            })
        } else {
            setFormData({
                ...formData,
                interfaces: [...(formData["interfaces"] as string[]), interfaceName]
            })
        }
    };

    const handleConfigurationChange = (index: number, property: string, value: string) => {
        setAdditionalConfigurations(prev => {
            const entries = Object.entries(prev[index]);
            const updatedEntries = entries.map(([key, val]) => {
                if ('key' === property) {
                    return [value, val];
                }
                return [key, value];
            });
            const updatedObject = Object.fromEntries(updatedEntries);
            return [
                ...prev.slice(0, index),
                updatedObject,
                ...prev.slice(index + 1)
            ];
        });
    }

    const handleDeleteConfiguration = (index: number) => {
        const filtered = additionalConfigurations.filter((_, i) => i !== index);
        setAdditionalConfigurations(filtered);
    };

    const handleCreateCatalog = async () => {
        if (!validateForm(componentType, formData, additionalConfigurations)) return;
        const serverData = getUpdatedFormData(additionalConfigurations, formData, componentType);
        setModalLoader(true);
        try {
            if (mode === 'Create') {
                await CatalogueService.addCatalogue(serverData)
                showToastMessage('success', `${componentType} added successfully`);
            } else if (editedCatalogData) {
                await CatalogueService.updateCatalogue(editedCatalogData?.uniqueId, serverData)
                showToastMessage('success', `${componentType} updated successfully`);
            }
            setRefreshCatalog();
        } catch (error: any) {
            console.error(error);
            const updated_mode = mode === 'Create' ? 'creating' : 'updating';
            const message = typeof error?.response?.data === 'string' ? error?.response?.data : error?.response?.data?.message || `Something went wrong while ${updated_mode} new ${componentType}`;
            showToastMessage('error', message);
            setModalLoader(false);
        }
    };

    return (
        <Fragment>
            <div className="create-payload d-flex flex-column justify-self-center">
                <div className="cp-header">
                    <div className="cp-header-title">
                        {mode === 'Create' ? 'Add New' : 'Update'} {componentType}
                    </div>
                    <div onClick={() => setOpenConfirmationDialog(true)}>
                        <CloseSvg />
                    </div>
                </div>
                <div><GradientBorderSvg /></div>
                <div className="cp-body">
                    <div className="w-100 d-flex flex-row gap-2">
                        <div className="w-100 d-flex flex-column">
                            <div className="cp-text">Name</div>
                            <div>
                                <input type="text" className="form-control"
                                    placeholder="Name"
                                    value={formData.name}
                                    onChange={(e) => {
                                        const inputValue = e.target.value.trimStart();
                                        handleNameChange('name', inputValue);
                                    }} />
                            </div>
                        </div>
                    </div>
                    <div className="w-100 d-flex flex-row gap-2">
                        <div className="w-50 d-flex flex-column">
                            <div className="cp-text">Size (U)</div>
                            <Form.Control
                                type="text"
                                required
                                placeholder="Size"
                                value={formData?.size}
                                onKeyDown={onKeyDown}
                                onChange={(event: any) => {
                                    onChangeSize(event.target.value);
                                }}
                            >
                            </Form.Control>
                        </div>
                        <div className="flex-1 d-flex flex-column">
                            <div className="cp-text">Mass (kg)</div>
                            <Form.Control
                                type="text"
                                required
                                placeholder="Mass"
                                value={formData?.mass}
                                onKeyDown={onKeyDown}
                                onChange={(event: any) => {
                                    updateField('mass', event.target.value);
                                }}
                            >
                            </Form.Control>
                        </div>
                    </div>

                    <div className="w-100 d-flex flex-row gap-2">
                        <div className="w-50 d-flex flex-column">
                            <div className="cp-text">Peak Power Consumption (W)</div>
                            <Form.Control
                                type="text"
                                required
                                placeholder="Peak Power Consumption"
                                value={formData?.peakPower}
                                onKeyDown={onKeyDown}
                                onChange={(event: any) => {
                                    updateField('peakPower', event.target.value);
                                }}
                            >
                            </Form.Control>

                        </div>
                        <div className="w-50 d-flex flex-column">
                            <div className="cp-text">Average Power Consumption (W)</div>
                            <Form.Control
                                type="text"
                                required
                                placeholder="Average Power Consumption"
                                value={formData?.averagePower}
                                onKeyDown={onKeyDown}
                                onChange={(event: any) => {
                                    updateField('averagePower', event.target.value);
                                }}
                            >
                            </Form.Control>
                        </div>
                    </div>

                    <Component
                        formData={formData}
                        setFormData={setFormData}
                        onChange={onChange}
                    />
                    {POWER_SINK_COMPONENTS.includes(catalogue_type) && catalogue_type !== 'iADCS' &&
                        <div className="d-flex flex-column gap-1">
                            <div className='component__separator' />
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="cp-text fs-14">Power Sinks</div>
                            </div>
                            {power_sinks?.length > 0 && power_sinks.map((_, componentIndex: number) => {
                                const label = `Power Sink ${componentIndex + 1}`
                                return (
                                    <div className="d-flex flex-column gap-1" key={componentIndex}>
                                        <div className="d-flex align-items-center justify-content-between position-relative">
                                            <span className='cp-text'>{label}</span>
                                            {(power_sinks?.length > 1 && power_sinks?.length <= 5) &&
                                                <button
                                                    className="h-100 px-2 py-4 w-auto bg-transparent border d-flex justify-content-center align-items-center position-absolute top-5px right-0"
                                                    onClick={() => { setFormData({ ...formData, power_sinks: power_sinks.filter((_: any, index: number) => index !== componentIndex) }) }}
                                                >
                                                    <i className="fe fe-x fs-16"></i>
                                                </button>
                                            }
                                        </div>
                                        <div className="d-flex align-items-start gap-2">
                                            {DEFAULT_POWER_SINK_FIELDS.map((field: any, index: number) => (
                                                <div key={index} className="w-25 d-flex flex-row gap-2">
                                                    <div className="w-100 d-flex flex-column">
                                                        <div className="cp-text">{field.label}</div>
                                                        <Form.Control
                                                            type="text"
                                                            required
                                                            placeholder={field.label}
                                                            value={power_sinks[componentIndex]?.[field.key]}
                                                            onKeyDown={onKeyDown}
                                                            onChange={(event: any) => {
                                                                onChangePowerSink(componentIndex, field.key, event.target.value);
                                                            }}
                                                        >
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })}
                            <button
                                className="cp-add-btn w-100 py-2 d-flex align-items-center justify-content-center mt-4"
                                disabled={power_sinks?.length === 5 || disabled_add_power_sinks_button}
                                onClick={() => { setFormData({ ...formData, power_sinks: [...power_sinks, DEFAULT_POWER_SINK_DATA] }) }}
                            >
                                <i className="fe fe-plus fs-16"></i>
                            </button>
                            <div className='component__separator mt-3' />
                        </div>
                    }

                    {componentType !== CatalogConstants.BusComponents &&
                        <div className="w-100 d-flex flex-row">
                            <div className="d-flex flex-column gap-1">
                                <div className="cp-text">Interfaces</div>
                                <div className="d-flex flex-row flex-wrap gap-2">
                                    {DEFAULT_INTERFACES[componentType].map((interfaceName: string, index: number) => (
                                        <div
                                            key={index}
                                            onClick={() => toggleSelection(interfaceName)}
                                            className={`multi__group__selection__button ${(formData["interfaces"] || []).includes(interfaceName) && "active"}`}
                                        >
                                            {interfaceName}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }

                    <div className="w-100 d-flex flex-column gap-2">
                        <div className="cp-text">{componentType !== CatalogConstants.BusComponents && 'Additional'} Specifications</div>

                        <div className="d-flex flex-column gap-2">
                            {additionalConfigurations.map((config, i) => {
                                const key = Object.keys(config)[0];
                                const value = config[key];
                                return (
                                    <div key={i} className="d-flex flex-row gap-2 align-items-center justify-content-between">
                                        <div className="w-50">
                                            <input type="text" className="form-control" value={key}
                                                placeholder="Property Name"
                                                onChange={(e) => handleConfigurationChange(i, 'key', e.target.value)}
                                            />
                                        </div>
                                        <div className="w-50">
                                            <input type="text" className="form-control" value={value}
                                                placeholder="Property Value"
                                                onChange={(e) => handleConfigurationChange(i, 'value', e.target.value)}
                                            />
                                        </div>
                                        <button
                                            className="h-100 py-2 px-1 w-5 bg-transparent border d-flex justify-content-center align-items-center"
                                            onClick={() => handleDeleteConfiguration(i)}
                                        >
                                            <i className="fe fe-x fs-16"></i>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            disabled={has_invalid_specification}
                            className="cp-add-btn w-100 py-2 d-flex align-items-center justify-content-center"
                            onClick={() => {
                                setAdditionalConfigurations(prev => ([...prev, { '': '' }]))
                            }}
                        >
                            <i className="fe fe-plus me-2"></i>
                        </button>

                    </div>
                </div>

                <div><GradientBorderSvg /></div>
                <div className="cp-footer">
                    <div>
                        <button className="d-flex align-items-center btn btn-outline-danger pt-1 pb-1 pe-4 ps-4 fs-14 btn btn-outline-danger"
                            onClick={() => setOpenConfirmationDialog(true)}>
                            Cancel
                        </button>
                    </div>
                    <div>
                        <button
                            className={`d-flex align-items-center btn btn-primary gap-2 fs-14 catalog-create-btn`}
                            onClick={() => handleCreateCatalog()}
                            disabled={!isFormValid(formData, componentType) || modalLoader}
                        >
                            {modalLoader ? `${getLoadingButtonText(mode)}` : mode} {modalLoader && <div className="spin__loader" />}
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmationDialog
                type='cancel'
                title={`Exit ${componentType} ${mode}`}
                open={openConfirmationDialog}
                description={`All settings will be lost. Are you sure you want to exit?`}
                onCancel={() => {
                    setOpenConfirmationDialog(false)
                }}
                onConfirm={() => {
                    setOpenConfigurationModal(null);
                    setOpenConfirmationDialog(false)
                }}
                confirm_button_text={`Exit`}
            />
        </Fragment>
    )
};

export default CreateCatalog;